import { Label } from "layout/typography/Label";
import { AutoComplete } from "primereact/autocomplete";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { confirmDialog } from "primereact/confirmdialog";
import { RadioButton } from "primereact/radiobutton";
import { Tooltip } from "primereact/tooltip";
import { useMemo, useState } from "react";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

import { ALL_PAYERS_OPTION } from "../helpers/ruleApplicability";
import {
  IBucketPair,
  IPayerOption,
  IRuleBucketMember,
  pairKey,
} from "../helpers/ruleBucket";
import { IRuleBucket } from "../hooks/useRuleBucket";

const ScopeBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background: #f3f4f6;
  border-radius: 6px;
  margin-bottom: 1rem;
`;

const ScopeLine = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Hint = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

// Below its add-payer input, so the input lines up with the Provider field beside it.
const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const PayerChip = styled(Chip)`
  &.p-chip {
    padding-right: 0.25rem;
  }
  .p-button.p-button-icon-only {
    width: 1.75rem;
    height: 1.75rem;
  }
`;

const ProviderGroup = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
`;

const ProviderGroupTitle = styled.div`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const AddProviderRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 0.5rem;
  align-items: start;
`;

const LAST_MEMBER_TOOLTIP = "Use Delete to remove the rule entirely";
const LAST_MEMBER_TOOLTIP_TARGET = "bucket-last-payer-remove";

/**
 * CLE-2842 — how far an edit reaches: "Only this payer", "All payers of <provider>", "All
 * providers". Shown at the top of the provider/payer section of a bucket's edit page.
 */
export function RuleBucketScopeSelector({ bucket }: { bucket: IRuleBucket }) {
  return (
    <ScopeBox role="radiogroup" aria-label="Apply changes to">
      <span style={{ fontWeight: 600 }}>Apply changes to</span>
      {bucket.options.map((option) => (
        <ScopeLine key={option.value}>
          <RadioButton
            inputId={`bucketScope-${option.value}`}
            name="bucketScope"
            value={option.value}
            checked={bucket.scope === option.value}
            disabled={bucket.savingCount !== null}
            onChange={() => bucket.setScope(option.value)}
          />
          <label
            htmlFor={`bucketScope-${option.value}`}
            style={{ cursor: "pointer" }}
          >
            {option.label}
          </label>
        </ScopeLine>
      ))}
      {bucket.scope === "PAYER" && (
        <Hint>
          Only this payer&apos;s copy will change; it will no longer be grouped
          with the others.
        </Hint>
      )}
    </ScopeBox>
  );
}

/** A chip's remove button; the last surviving member's is disabled, with the reason. */
function RemoveButton({
  label,
  onClick,
  disabled = false,
  busy = false,
}: {
  label: string;
  onClick: () => void;
  /** The last surviving member: disabled, with the reason in a tooltip. */
  disabled?: boolean;
  /** A save is in flight: disabled, no tooltip. */
  busy?: boolean;
}) {
  const button = (
    <Button
      type="button"
      icon="pi pi-times-circle"
      className="p-button-rounded p-button-text p-button-secondary p-button-sm"
      aria-label={`Remove ${label}`}
      disabled={disabled || busy}
      onClick={onClick}
    />
  );
  if (!disabled) return button;
  // A disabled button gets no pointer events, so the tooltip hangs off a wrapper React owns.
  return (
    <span
      className={LAST_MEMBER_TOOLTIP_TARGET}
      data-pr-tooltip={LAST_MEMBER_TOOLTIP}
      data-pr-position="top"
    >
      {button}
    </span>
  );
}

/** Member chips (removable, removal asked first) plus the not-yet-saved added pairs. */
function BucketChips({
  bucket,
  members,
  added,
  labelOf,
  testId,
}: {
  bucket: IRuleBucket;
  members: IRuleBucketMember[];
  added: IBucketPair[];
  /** How a pair is named on its chip and in its remove button / confirmation. */
  labelOf: (pair: IBucketPair) => { chip: string; full: string };
  testId: string;
}) {
  const onlyOneMemberLeft = bucket.activeMembers.length <= 1;
  const saving = bucket.savingCount !== null;
  const confirmRemove = (member: IRuleBucketMember) => {
    const { full } = labelOf(member);
    confirmDialog({
      header: "Remove payer",
      icon: "pi pi-exclamation-triangle",
      message: `Remove ${full} from this rule? Its row, attachments and history will be deleted when you save.`,
      accept: () => bucket.removeMember(member.id),
    });
  };
  return (
    <ChipList data-testid={testId}>
      {members
        .filter((member) => !bucket.removedIds.includes(member.id))
        .map((member) => {
          const { chip, full } = labelOf(member);
          return (
            <PayerChip
              key={member.id}
              template={
                <>
                  <span className="p-chip-text">{chip}</span>
                  <RemoveButton
                    label={full}
                    onClick={() => confirmRemove(member)}
                    disabled={onlyOneMemberLeft}
                    busy={saving}
                  />
                </>
              }
            />
          );
        })}
      {added.map((pair) => {
        const { chip, full } = labelOf(pair);
        return (
          <PayerChip
            key={`added-${pairKey(pair)}`}
            template={
              <>
                <span className="p-chip-text">{`${chip} (new)`}</span>
                <RemoveButton
                  label={full}
                  onClick={() => bucket.removeAddedPair(pair)}
                  busy={saving}
                />
              </>
            }
          />
        );
      })}
    </ChipList>
  );
}

/** Every payer option (All payers first) not already covered for `providerId`. */
function usePayerOptions(bucket: IRuleBucket, providerId: string | null) {
  const { data: allPayers } = useGetAllPayersQuery();
  return useMemo(() => {
    const taken = new Set(
      bucket.activePairs
        .filter((pair) => pair.providerId === providerId)
        .map((pair) => pair.payerId)
    );
    const options: IPayerOption[] = [
      ALL_PAYERS_OPTION,
      ...(allPayers?.data ?? []).map((payer: any) => ({
        name: payer.name,
        value: payer.id,
      })),
    ];
    return options.filter((option) => !taken.has(option.value));
  }, [allPayers, bucket.activePairs, providerId]);
}

/** An autocomplete that adds one payer to `provider` on select. */
function AddPayerAutocomplete({
  bucket,
  providerId,
  providerName,
  inputId,
  ariaLabel,
}: {
  bucket: IRuleBucket;
  providerId: string;
  providerName: string;
  inputId: string;
  ariaLabel: string;
}) {
  const options = usePayerOptions(bucket, providerId);
  const [query, setQuery] = useState<string | IPayerOption>("");
  const [suggestions, setSuggestions] = useState<IPayerOption[]>([]);
  const search = (event: { query: string }) => {
    const text = event.query.trim().toLowerCase();
    setSuggestions(
      text
        ? options.filter((payer) => payer.name.toLowerCase().startsWith(text))
        : [...options]
    );
  };
  return (
    <AutoComplete
      dropdown
      field="name"
      inputId={inputId}
      value={query}
      className="w-full"
      suggestions={suggestions}
      completeMethod={search}
      dropdownAriaLabel={ariaLabel}
      placeholder="Add payer"
      disabled={bucket.savingCount !== null}
      onChange={(e) => setQuery(e.value)}
      onSelect={(e) => {
        const payer = e.value as IPayerOption;
        bucket.addPair({
          providerId,
          providerName,
          payerId: payer.value,
          payerName: payer.name,
        });
        setQuery("");
      }}
    />
  );
}

/**
 * "All payers of <provider>": this provider's payers as removable chips, plus an autocomplete to
 * add more. Removing a member only marks its row for deletion — nothing changes until the save.
 */
export function RuleBucketPayersField({ bucket }: { bucket: IRuleBucket }) {
  return (
    <>
      <Label htmlFor="addBucketPayer">Payers</Label>
      <Tooltip target={`.${LAST_MEMBER_TOOLTIP_TARGET}`} />
      <AddPayerAutocomplete
        bucket={bucket}
        providerId={bucket.providerId}
        providerName={bucket.providerName}
        inputId="addBucketPayer"
        ariaLabel="Add Payer"
      />
      <BucketChips
        bucket={bucket}
        members={bucket.scopeMembers}
        added={bucket.added}
        labelOf={(pair) => ({ chip: pair.payerName, full: pair.payerName })}
        testId="bucket-payer-chips"
      />
    </>
  );
}

/** Adds a provider with one or more payers (All payers included) in one go. */
function AddProviderRowField({ bucket }: { bucket: IRuleBucket }) {
  const { data: allProviders } = useGetAllProvidersQuery();
  const [provider, setProvider] = useState<
    string | { name: string; value: string }
  >("");
  const [providerSuggestions, setProviderSuggestions] = useState<
    { name: string; value: string }[]
  >([]);
  const [payers, setPayers] = useState<IPayerOption[]>([]);
  const [payerSuggestions, setPayerSuggestions] = useState<IPayerOption[]>([]);
  const chosenProvider = typeof provider === "object" ? provider : null;
  const payerOptions = usePayerOptions(bucket, chosenProvider?.value ?? null);

  const providerOptions = useMemo(
    () =>
      (allProviders?.data ?? []).map((p: any) => ({
        name: p.name,
        value: p.id,
      })),
    [allProviders]
  );

  const searchProvider = (event: { query: string }) => {
    const text = event.query.trim().toLowerCase();
    setProviderSuggestions(
      text
        ? providerOptions.filter((p: { name: string }) =>
            p.name.toLowerCase().startsWith(text)
          )
        : [...providerOptions]
    );
  };
  const searchPayer = (event: { query: string }) => {
    const text = event.query.trim().toLowerCase();
    const chosen = new Set(payers.map((payer) => payer.value));
    const available = payerOptions.filter((payer) => !chosen.has(payer.value));
    setPayerSuggestions(
      text
        ? available.filter((payer) => payer.name.toLowerCase().startsWith(text))
        : available
    );
  };

  const add = () => {
    if (!chosenProvider || !payers.length) return;
    payers.forEach((payer) =>
      bucket.addPair({
        providerId: chosenProvider.value,
        providerName: chosenProvider.name,
        payerId: payer.value,
        payerName: payer.name,
      })
    );
    setProvider("");
    setPayers([]);
  };

  return (
    <div data-testid="bucket-add-provider">
      <Label htmlFor="addBucketProvider">Add provider</Label>
      <AddProviderRow>
        <AutoComplete
          dropdown
          field="name"
          inputId="addBucketProvider"
          value={provider}
          className="w-full"
          suggestions={providerSuggestions}
          completeMethod={searchProvider}
          dropdownAriaLabel="Add Provider"
          placeholder="Provider"
          disabled={bucket.savingCount !== null}
          onChange={(e) => setProvider(e.value)}
        />
        <AutoComplete
          dropdown
          multiple
          field="name"
          inputId="addBucketProviderPayers"
          value={payers}
          className="w-full"
          suggestions={payerSuggestions}
          completeMethod={searchPayer}
          dropdownAriaLabel="Add Provider Payers"
          placeholder="Payers"
          disabled={bucket.savingCount !== null}
          onChange={(e) => setPayers(e.value ?? [])}
        />
        <Button
          type="button"
          label="Add"
          icon="pi pi-plus"
          className="p-button-sm"
          disabled={
            !chosenProvider || !payers.length || bucket.savingCount !== null
          }
          onClick={add}
        />
      </AddProviderRow>
    </div>
  );
}

/**
 * "All providers": the bucket's members grouped by provider — each provider's payers as removable
 * chips (the last member overall cannot be removed) and an autocomplete to add a payer — plus
 * "Add provider". Added pairs and removals are only sent on save.
 */
export function RuleBucketProvidersField({ bucket }: { bucket: IRuleBucket }) {
  const groups = useMemo(() => {
    const byProvider = new Map<
      string,
      { providerId: string; providerName: string; members: IRuleBucketMember[] }
    >();
    bucket.scopeMembers.forEach((member) => {
      const group = byProvider.get(member.providerId) ?? {
        providerId: member.providerId,
        providerName: member.providerName,
        members: [],
      };
      group.members.push(member);
      byProvider.set(member.providerId, group);
    });
    bucket.added.forEach((pair) => {
      if (!byProvider.has(pair.providerId)) {
        byProvider.set(pair.providerId, {
          providerId: pair.providerId,
          providerName: pair.providerName,
          members: [],
        });
      }
    });
    return [...byProvider.values()];
  }, [bucket.scopeMembers, bucket.added]);

  return (
    <div data-testid="bucket-providers">
      <div style={{ fontWeight: 600, marginBottom: "0.5rem" }}>
        Providers and payers
      </div>
      <Tooltip target={`.${LAST_MEMBER_TOOLTIP_TARGET}`} />
      {groups.map((group) => (
        <ProviderGroup
          key={group.providerId}
          data-testid={`bucket-provider-${group.providerId}`}
        >
          <ProviderGroupTitle>{group.providerName}</ProviderGroupTitle>
          <AddPayerAutocomplete
            bucket={bucket}
            providerId={group.providerId}
            providerName={group.providerName}
            inputId={`addBucketPayer-${group.providerId}`}
            ariaLabel={`Add payer to ${group.providerName}`}
          />
          <BucketChips
            bucket={bucket}
            members={group.members}
            added={bucket.added.filter(
              (pair) => pair.providerId === group.providerId
            )}
            labelOf={(pair) => ({
              chip: pair.payerName,
              full: `${pair.providerName} / ${pair.payerName}`,
            })}
            testId={`bucket-provider-chips-${group.providerId}`}
          />
        </ProviderGroup>
      ))}
      <AddProviderRowField bucket={bucket} />
    </div>
  );
}
